import { Link, Navigate, Outlet, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { Baby, Bell, CalendarHeart, CheckSquare, Download, Handshake, Inbox, LayoutDashboard, Repeat, Settings, Users } from 'lucide-react';
import { useFamily, usePendingDecisions, useSyncPolling } from '../api/queries';
import { useSession } from '../api/session';
import { Logo } from '../components/Logo';
import { Avatar, Spinner } from '../components/ui';

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, mobile: true },
  { to: '/pool', label: 'House Pool', icon: Inbox, mobile: true },
  { to: '/mine', label: 'My Tasks', icon: CheckSquare, mobile: true },
  { to: '/partner', label: 'Partner', icon: Users, mobile: false },
  { to: '/children', label: 'Children', icon: Baby, mobile: true },
  { to: '/recurring', label: 'Recurring', icon: Repeat, mobile: false },
  { to: '/activities', label: 'Activities', icon: CalendarHeart, mobile: false },
  { to: '/decisions', label: 'Pending Decisions', icon: Handshake, mobile: true, badge: 'decisions' },
  { to: '/notifications', label: 'Notifications', icon: Bell, mobile: false, badge: 'notifications' },
  { to: '/settings', label: 'Family Settings', icon: Settings, mobile: false },
  { to: '/download', label: 'Download App', icon: Download, mobile: false },
] as const;

/** Guards: signed in, and a member of a household (otherwise onboarding). */
export function AppLayout() {
  const { status, household } = useSession();
  // Captured once: while the router transitions to /login, this layout re-renders with the *new* location, and
  // reading it live would redirect again to /login?redirect=/login?redirect=… until the tab ran out of memory.
  const router = useRouter();
  const [returnTo] = useState(() => router.state.location.href);
  if (status === 'loading') return <Spinner />;
  if (status === 'anonymous') return <Navigate to="/login" search={{ redirect: returnTo.startsWith('/login') ? undefined : returnTo }} replace />;
  if (!household) return <Navigate to="/onboarding" />;
  return <Shell />;
}

function Shell() {
  const { me, logout } = useSession();
  const { unreadNotifications } = useSyncPolling();
  const decisions = usePendingDecisions().data?.count ?? 0;
  const family = useFamily().data;
  const badge = (b?: string) => (b === 'decisions' ? decisions : b === 'notifications' ? unreadNotifications : 0);

  return (
    <div className="min-h-dvh md:flex">
      <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 flex-col border-r border-[var(--border)] bg-[var(--surface)] p-4 md:flex">
        <Link to="/" className="mb-6 flex items-center gap-2.5 px-2">
          <Logo size={32} />
          <span className="min-w-0 truncate text-lg font-semibold">{family?.name ?? 'FamilyChores'}</span>
        </Link>
        <nav className="flex-1 space-y-0.5" aria-label="Main">
          {NAV.map((item) => (
            <NavItem key={item.to} {...item} count={badge('badge' in item ? item.badge : undefined)} />
          ))}
        </nav>
        <div className="mt-4 flex items-center gap-2 border-t border-[var(--border)] pt-4 text-sm">
          <Avatar name={me?.displayName ?? '?'} />
          <Link to="/account" className="min-w-0 flex-1 truncate hover:underline">
            {me?.displayName}
          </Link>
          <button className="text-xs text-[var(--muted)] hover:underline" onClick={() => void logout()}>
            Sign out
          </button>
        </div>
      </aside>

      <main className="mx-auto w-full max-w-4xl flex-1 px-4 pb-24 pt-5 md:px-8 md:pb-10 md:pt-8">
        <div className="mb-4 flex items-center justify-between md:hidden">
          <span className="flex min-w-0 items-center gap-2 font-semibold">
            <Logo size={28} />
            <span className="truncate">{family?.name ?? 'FamilyChores'}</span>
          </span>
          <div className="flex items-center gap-3">
            <Link to="/notifications" className="relative" aria-label="Notifications">
              <Bell size={20} />
              {unreadNotifications ? <Dot count={unreadNotifications} /> : null}
            </Link>
            <Link to="/settings" aria-label="Family settings">
              <Settings size={20} />
            </Link>
          </div>
        </div>
        <Outlet />
      </main>

      <nav aria-label="Main" className="fixed inset-x-0 bottom-0 z-30 flex border-t border-[var(--border)] bg-[var(--surface)] md:hidden">
        {NAV.filter((n) => n.mobile).map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className="relative flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] text-[var(--muted)]"
            activeProps={{ className: 'text-[var(--accent)]' }}
            activeOptions={{ exact: item.to === '/' }}
          >
            <item.icon size={20} />
            {item.label.replace('Pending ', '')}
            {badge('badge' in item ? item.badge : undefined) ? <Dot count={badge('badge' in item ? item.badge : undefined)} /> : null}
          </Link>
        ))}
      </nav>
    </div>
  );
}

function NavItem({ to, label, icon: Icon, count }: { to: string; label: string; icon: typeof Inbox; count: number }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-[var(--muted)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
      activeProps={{ className: 'bg-[var(--accent-soft)] !text-[var(--accent)] font-medium' }}
      activeOptions={{ exact: to === '/' }}
    >
      <Icon size={18} />
      <span className="flex-1">{label}</span>
      {count ? <span className="rounded-full bg-[var(--accent)] px-1.5 text-xs text-white">{count}</span> : null}
    </Link>
  );
}

function Dot({ count }: { count: number }) {
  return (
    <span className="absolute -right-1 -top-1 min-w-4 rounded-full bg-[var(--accent)] px-1 text-center text-[10px] leading-4 text-white">
      {count > 9 ? '9+' : count}
    </span>
  );
}
